import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import { closeModal } from 'modal/store/actions';
import Title3 from 'base/components/Title3';
import MainTitle from 'base/components/MainTitle';
import FieldWrapper from 'base/components/forms/FieldWrapper';
import Button from 'base/components/Button';
import Col from 'base/components/Col';
import Row from 'base/components/Row';
import ModalHeader from 'modal/components/ModalHeader';
import { getSelectedDocuments } from 'documents/store/fileexplorer/selectors';
import { getActiveDocument } from 'documents/store/documents/selectors';
import FormikInputField from 'base/components/forms/formik/FormikInputField';
import SubmitButton from 'base/components/forms/SubmitButton';
import Spinner from 'base/components/Spinner';
import {
  getCurrentlyCachingOfflineDocuments,
  getCurrentlyCachingOfflineDocumentIdsWithIncorrectPassword,
} from '../../store/selectors';
import { cacheDocumentsOffline, cacheDocumentsOfflineAbort } from '../../store/actions';
import theme from '../../../theme';

const DocumentCacheOfflineModal = ({
  name,
  closeModal,
  cacheDocumentsOffline,
  selectedDocuments,
  cacheDocumentsOfflineAbort,
  currentlyCachingDocuments,
  documentIdsWithIncorrectPassword,
}) => {
  // Save documents in state since a bug in the bulk selection
  // resets the selection during the offline caching
  const [documents] = useState(selectedDocuments);

  const initialConfirmation = (
    <>
      <FieldWrapper>
          Offline documents are stored on your device. Only use offline documents on your
          own secure device, not a shared device.
        <br />
          Please note that the offline mode is not supported by Internet Explorer.
      </FieldWrapper>

      <Row>
        <Col>
          <Button
            isFullWidth
            variant="secondary"
            onClick={() => { closeModal(name); cacheDocumentsOfflineAbort(); }}
          >
              No, cancel
          </Button>
        </Col>
        <Col>
          <Button
            isFullWidth
            variant="secondary"
            onClick={() => { cacheDocumentsOffline(documents); }}
          >
              Yes, I am on a secure device
          </Button>
        </Col>
      </Row>
    </>
  );

  const loadingIndicator = (
    <FieldWrapper>
      This may take a moment.
      <br />
      {' '}
      <br />
      <Spinner color={theme.colors.light} />
    </FieldWrapper>
  );

  const passwordForm = documentIdsWithIncorrectPassword.length > 0 && documents.length > 0 && (
    <>
      <FieldWrapper>
        <Title3>
          PDF Password required for
          &quot;
          {documents.find((d) => d.id && d.id === documentIdsWithIncorrectPassword[0]).name}
&quot;
        </Title3>
        <small>
          You have to unlock this document in order to save it for offline usage.
          Please enter the password in the field below.
        </small>
      </FieldWrapper>
      <Formik
        initialValues={{
          password: '',
        }}
        validationSchema={
          Yup.object().shape({
            password: Yup.string().required('PDF password is required.'),
          })
        }
        render={({errors, isSubmitting}) => ( // eslint-disable-line
          <Form>
            <Field
              type="password"
              name="password"
              placeholder="PDF password"
              component={FormikInputField}
              autoFocus
            />
            <SubmitButton type="submit" disabled={isSubmitting}>
                Save offline
            </SubmitButton>
          </Form>
        )}
        onSubmit={(values) => {
          // cache remaining documents
          cacheDocumentsOffline(currentlyCachingDocuments, values.password);
        }}
      />
    </>
  );

  const cachingContent = documentIdsWithIncorrectPassword.length > 0
    ? passwordForm
    : loadingIndicator;

  return (
    <>
      <ModalHeader>
        <MainTitle>
Save document
          {documents.length > 1 && 's'}
          {' '}
offline
        </MainTitle>
      </ModalHeader>

      <FieldWrapper>
        {documents && documents.map((document) => (
          <Title3 key={document.id}>{document.name}</Title3>
        ))}
      </FieldWrapper>

      {currentlyCachingDocuments.length > 0 ? cachingContent : initialConfirmation}
    </>
  );
};
DocumentCacheOfflineModal.propTypes = {
  name: PropTypes.string.isRequired,
  selectedDocuments: PropTypes.arrayOf(PropTypes.any),
  closeModal: PropTypes.func.isRequired,
  cacheDocumentsOffline: PropTypes.func.isRequired,
  cacheDocumentsOfflineAbort: PropTypes.func.isRequired,
  currentlyCachingDocuments: PropTypes.arrayOf(PropTypes.any).isRequired,
  documentIdsWithIncorrectPassword: PropTypes.arrayOf(PropTypes.any).isRequired,
};
DocumentCacheOfflineModal.defaultProps = {
  selectedDocuments: [],
};

const mapStateToProps = (state) => ({
  // documents can either come from a bulk selection
  // or from the current active document if the button has been clicked on a single document
  selectedDocuments: getSelectedDocuments(state).length > 0 // eslint-disable-line
    ? getSelectedDocuments(state)
    : (getActiveDocument(state) ? [getActiveDocument(state)] : []),
  currentlyCachingDocuments: getCurrentlyCachingOfflineDocuments(state),
  documentIdsWithIncorrectPassword:
    getCurrentlyCachingOfflineDocumentIdsWithIncorrectPassword(state),
});

const mapDispatchToProps = {
  cacheDocumentsOffline,
  cacheDocumentsOfflineAbort,
  closeModal,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DocumentCacheOfflineModal);
